import React, {ReactNode} from "react";
import {Button} from "primereact/button";
import Exception from "../../../shared/exception/Exception";
import MessageBox from "../../components/MessageBox";
import {I18nGlobals} from "../../translation/i18nInterface";
import {MessageType} from "../../log/MessageInterface";
import AbstractContext from "../../AbstractContext";
import ErrorCode from "../../exception/ErrorCode";
import {HelperGlobals} from "../../components/Helper";
import Translate from "../../translation/Translate";
import FunctionHelper from "../../helper/FunctionHelper";
import {Information, PortalFunctions} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {isMobile} from "react-device-detect";
import PortalComponent from "../../components/PortalComponent";

export interface AbstractDialogProperties extends I18nGlobals, HelperGlobals {
	context: AbstractContext<any, any>;
	visible: boolean;
	hide: () => void;
}

export interface AbstractDialogState {
	exception?: Exception;
	info?: string;
	submitted: boolean;
	visible: boolean;
}

export default abstract class AbstractDialog<PROPERTY_TYPE extends AbstractDialogProperties = any,
	STATUS_TYPE extends AbstractDialogState = any> extends PortalComponent<PROPERTY_TYPE, STATUS_TYPE> {
	/**
	 * Initialize an AbstractDialog component instance
	 */
	constructor(props: Readonly<PROPERTY_TYPE>) {
		super(props);

		this.state = {
			submitted: false,
			visible: false
		} as STATUS_TYPE;

		this.hide = this.hide.bind(this);
		this.onShow = this.onShow.bind(this);
		this.onError = this.onError.bind(this);
		this.renderError = this.renderError.bind(this);
		this.renderInfo = this.renderInfo.bind(this);
		this.renderHelpButton = this.renderHelpButton.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<PROPERTY_TYPE>, prevState: Readonly<STATUS_TYPE>, snapshot?: any
	): Promise<void> {
		if (prevProps.visible !== this.props.visible && this.props.visible) {
			await this.onShow();
		}
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		window.addEventListener('keydown', this.handleKeydown);

		if (this.props.visible) {
			await this.onShow();
		}
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();
		window.removeEventListener('keydown', this.handleKeydown);
	}

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		this.setState({
			info: undefined,
			exception: undefined,
			submitted: true
		});
	}

	/**
	 * Handle closing/hiding the dialog
	 */
	protected hide(): void {
		this.setState({
			visible: false,
			submitted: false,
			exception: undefined,
			info: undefined
		});

		this.props.hide();
	}

	protected renderFieldError(value: string): ReactNode {
		if (!isMobile || FunctionHelper.isEmpty(value)) {
			return null;
		}

		return (
			<span className="field-error">{value}</span>
		);
	}

	/**
	 * Render dialog errors
	 */
	protected renderError(error?: Exception): ReactNode {
		let exception: Exception | undefined = error || this.state.exception;

		if (!FunctionHelper.exists(exception)) {
			return null;
		}

		let codes: string = "";
		if (FunctionHelper.exists(exception!.code)) {
			codes += "(errorcode: " + exception!.code;

			if (FunctionHelper.exists(exception!.exitCode)) {
				codes += ", exitcode: " + exception!.exitCode;
			}

			codes += ")";
		}

		return (
			<div className="p-col-12">
				<MessageBox {...this.props} type={MessageType.DANGER} closeable={true}
							onClose={() => this.setState({
								exception: undefined
							})}
							stacktrace={
								<>
									<p>{exception!.message} {codes}</p>
									<pre>{exception!.stack}</pre>
								</>
							}>
					{exception!.message}
				</MessageBox>
			</div>
		);
	}

	/**
	 * Render dialog errors
	 */
	protected renderInfo(): ReactNode {
		let info: string | undefined = this.state.info;

		if (!FunctionHelper.exists(info)) {
			return null;
		}

		return (
			<div className="p-col-12">
				<MessageBox {...this.props} type={MessageType.INFO} closeable={true}
							onClose={() => this.setState({
								info: undefined
							})}>
					{info}
				</MessageBox>
			</div>
		);
	}

	protected renderHelpButton(): ReactNode {
		let portalInfo: Information = this.props.context.info;

		if (!FunctionHelper.applyValue(
			portalInfo.functionsStatus?.showHelp, PortalFunctions.getShowHelpDefault()
		)) {
			return null;
		}

		return (
			<Button icon="icon icon-help" className={this.props.showHelp ? " active" : ""}
					onClick={() => {
						this.props.context.getPage().setState({
							showHelp: !this.props.showHelp,
						})
					}}/>
		);
	}

	/**
	 * Render the dialog header
	 */
	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>Dialog headline</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
							onClick={() => this.hide()} className="dialog-button"/>
				</div>
			</div>
		);
	}

	protected async handleKeydown(event: KeyboardEvent): Promise<void> {
		// no default behaviour
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		// init dialog
	}

	/**
	 * Handles a dialog exception and returns if the operation can be retried
	 */
	protected async onError(exception: Error): Promise<boolean> {
		let portalException: Exception = Exception.parseException(exception);

		if (portalException.name === "ERR_NETWORK") {
			await this.props.context.session.reconnectServer();
			return false;
		}

		switch (portalException.code) {
			case ErrorCode.WEBSERVICE_INVALID_DOCUMENT_ID:
				window.location.reload();
				break;
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_FOUND:
			case ErrorCode.WEBSERVICE_SESSION_EXPIRED:
			case ErrorCode.WEBSERVICE_SESSION_UNABLE_TO_CREATE:
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_ALLOWED:
			case ErrorCode.WEBSERVICE_SESSION_UNKNOWN_ERROR:
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_PROVIDED:
				this.setState({
					submitted: false,
					exception: portalException
				});

				this.props.hide();
				break;
			case ErrorCode.WEBSERVICE_SESSION_TIMEOUT:
				try {
					await this.props.context.session.refresh();
					return true;
				} catch (exception: any) {
					await this.onError(exception);
				}
				break;
			default:
				this.setState({
					submitted: false,
					exception: portalException
				});
				break;
		}

		return false;
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		return (
			<>
				{this.renderDialog()}
			</>
		);
	}

	protected abstract isValid(): boolean;

	/**
	 * render component as react node
	 */
	protected abstract renderDialog(): ReactNode;
}